import React from 'react';

// Catches a render error from one page instead of letting it take the whole
// app down.
//
// WHY THIS EXISTS: there was no boundary anywhere in the tree. In React 18 an
// uncaught render error unmounts the ENTIRE root, and because the router lives
// above the crash, navigation keeps "working" — the URL changes on every click
// while nothing renders, because there is no longer a React tree to render
// into. That is the "I click a menu item, the address bar changes, the page
// doesn't" symptom, and it makes a fault on one page look like the whole
// application has seized.
//
// Two jobs, in order of importance:
//   1. Contain the failure so the sidebar still works and the rest of the app
//      stays usable.
//   2. SHOW the error. A white screen tells the user nothing and tells us
//      nothing; the message and component stack are what actually get it
//      fixed, so they're on the page behind a disclosure rather than only in a
//      console nobody had open at the time.
//
// `resetKey` — pass the current pathname. When it changes the boundary clears
// itself, so navigating away from a broken page recovers without a reload.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Keep the console record too — the stack there is richer than what we
    // render, and it's what a screen-share will land on.
    // eslint-disable-next-line no-console
    console.error('Page crashed:', error, info?.componentStack);
  }

  componentDidUpdate(prev) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, info: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    const { error, info } = this.state;
    return (
      <div className="min-h-screen bg-canvas flex items-start justify-center px-6 py-16">
        <div className="w-full max-w-[640px] bg-white border border-line rounded-2xl px-7 py-7">
          <p className="font-['Metropolis'] text-[11px] font-bold tracking-[0.18em] uppercase text-[#B01F06]">
            This page hit a problem
          </p>
          <h1 className="font-['Metropolis'] text-2xl font-bold text-ink mt-2 leading-tight">
            Something on this page stopped working
          </h1>
          <p className="font-['Metropolis'] text-sm text-[#51534A] mt-3 leading-relaxed">
            Nothing you were doing has been saved. The rest of Supply Pilot is still
            working — use the menu to go somewhere else, or reload to try this page again.
          </p>

          <div className="flex items-center gap-3 mt-5 flex-wrap">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-xl bg-[#92D400] text-[#131313] font-['Metropolis'] text-sm font-bold px-5 py-2.5 transition-colors hover:bg-[#a4e810] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest">
              Reload this page
            </button>
            <button
              type="button"
              onClick={() => { window.location.href = '/dashboard'; }}
              className="rounded-xl bg-white border border-line text-ink font-['Metropolis'] text-sm font-semibold px-5 py-2.5 transition-colors hover:border-[#C9CFB6] hover:bg-[#FAFBF4] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest">
              Back to dashboard
            </button>
          </div>

          {/* The actual fault, on the page. Collapsed so it doesn't shout at
              someone who only wants to get on with their day, but present so
              it can be read out or screenshotted without opening devtools. */}
          <details className="mt-6">
            <summary className="font-['Metropolis'] text-xs font-semibold text-[#51534A] cursor-pointer hover:text-fusion-forest">
              Technical detail (useful when reporting this)
            </summary>
            <p className="font-['Metropolis'] text-[13px] font-semibold text-[#7A1B05] mt-2 break-words">
              {String(error?.message || error)}
            </p>
            {info?.componentStack && (
              <pre className="mt-2 max-h-64 overflow-auto bg-[#FAFBF4] border border-line rounded-lg p-3 text-[11px] leading-relaxed text-[#51534A] whitespace-pre-wrap break-words">
                {info.componentStack.trim()}
              </pre>
            )}
          </details>
        </div>
      </div>
    );
  }
}
